// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IntervalType } from '@/costs/utils/date-utils';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { getInventoryBridgeData } from '@/report/api/get-inventory-bridge-data';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

interface UseInventoryBridgeDataParams {
  startDate?: string;
  endDate?: string;
  interval: IntervalType;
  currency: IsoCurrencyCode;
  /** Callers gate on the resolved currency so a provisional one does not spend a fetch. */
  enabled?: boolean;
}

export function useInventoryBridgeData(params: UseInventoryBridgeDataParams) {
  // Check if both dates are defined for the enabled condition
  const areDatesValid = !!(params.startDate && params.endDate);

  const query = useQuery({
    queryKey: ['asset-bridge', 'inventory', params],
    queryFn: () => {
      // TypeScript now knows these are defined due to the enabled condition
      if (!params.startDate || !params.endDate) {
        throw new Error('Start date and end date are required');
      }

      return getInventoryBridgeData({
        startDate: DateTime.fromISO(params.startDate),
        endDate: DateTime.fromISO(params.endDate),
        interval: params.interval,
        currency: params.currency,
      });
    },
    enabled: areDatesValid && params.enabled !== false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
